<template>
    <div class="tw:space-y-4">
        <div class="tw:mb-4 tw:px-6">
            <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">
                Abonos
            </h2>
            <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">
                Abonos asociados al usuario
            </p>
        </div>

        <SimpleTable
            :flat="true"
            :columns="columns"
            :data="items"
            :skeleton="loading"
            :skeleton-rows="5"
            empty-text="No tiene abonos asociados"
            :default-col-width="defaultColWidth"
        >
            <template #skeleton-header-activo>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-header-n_abonado>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-nombre>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-apellidos>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-dni>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-header-email>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-10"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>

            <template #skeleton-activo>
                <div>
                    <div class="tw:h-3 tw:w-3 tw:bg-gray-200 tw:rounded-full"></div>
                </div>
            </template>
            <template #skeleton-n_abonado>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-nombre>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-10"></div>
            </template>
            <template #skeleton-apellidos>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-12"></div>
            </template>
            <template #skeleton-dni>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-email>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-48"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-8 tw:w-24 tw:bg-gray-200 tw:rounded"></div>
            </template>

            <template #cell-activo="{ row }">
                <div class="tw:flex tw:justify-center">
                    <span
                        class="tw:inline-block tw:w-3 tw:h-3 tw:rounded-full"
                        :class="activoClass(row.activo)"
                    ></span>
                </div>
            </template>

            <template #cell-opciones="{ row }">
                <OpcionesDropdownTableComponent
                    :options="getRowOptions(row)"
                />
            </template>
        </SimpleTable>

        <TablePagination
            v-model="currentPage"
            :total="total"
            :per-page="perPage"
        />
    </div>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue';
    import TablePagination from '@/modules/admin/ui/table/TablePagination.vue';
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue';
    import usuariosService from '@/services/admin/bases/usuariosService';

    const props = defineProps({
        listaId: { type: [String, Number], required: true },
        userId: { type: [String, Number], required: true },
        defaultColWidth: {
            type: String,
            default: '130px'
        }
    });

    const items = ref([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const total = ref(0);
    const perPage = ref(10);

    const columns = [
        { key: 'activo', label: 'activo', centered: true, class: 'tw:w-24' },
        { key: 'n_abonado', label: 'Nº de Abonado' },
        { key: 'nombre', label: 'Nombre' },
        { key: 'apellidos', label: 'Apellidos', class: 'tw:w-48' },
        { key: 'dni', label: 'DNI' },
        { key: 'email', label: 'Email', class: 'tw:w-64' },
        { key: 'opciones', label: 'Opciones', centered: true  },
    ];

    function activoClass(value) {
        switch (value) {
            case 1: return 'tw:bg-green-500';
            case 2: return 'tw:bg-blue-500';
            default: return 'tw:bg-red-500';
        }
    }

    function getRowOptions(row) {
        return [
            {
                to: {
                    name: 'back.abonados.show',
                    params: { lista_id: props.listaId, abonado_id: row.id },
                },
                label: 'Ver abono',
            },
            {
                type: 'action',
                action: () => window.open(row.url_imprimir, '_blank'),
                label: 'Imprimir',
            },
        ];
    }

    async function fetchAbonos() {
        loading.value = true;

        try {
            const result = await usuariosService.getAbonos({
                lista_id: props.listaId,
                user_id: props.userId,
                page: currentPage.value,
                per_page: perPage.value,
            });
            items.value = result.items;
            total.value = result.meta.total;
        } catch (err) {
            console.error('Error al cargar abonos:', err);
            items.value = [];
        } finally {
            loading.value = false;
        }
    }

    watch(currentPage, () => {
        fetchAbonos();
    });

    onMounted(() => {
        fetchAbonos();
    });
</script>
